import React, { useEffect, useState } from 'react';
import { PRODUCT_TYPE_COMPOSITE, PRODUCT_TYPE_SIMPLE } from '../../../Utils/Constants/Product';
import { useTranslation } from 'react-i18next';
import { useOrderTypeContext } from '../../../Contexts/OrderTypeContext';
import { useStoreContext } from '../../../Contexts/StoreContext';
import { useCartContext } from '../../../Contexts/CartContext';
import { useAddProductContext } from '../../../Contexts/AddProductContext';
import ButtonOpenStoreCounter from '../ButtonOpenStoreCounter';
import ButtonsIncDec from '../ButtonsIncDec';
import ButtonAddProduct from './ButtonAddProduct';
import { HiXMark } from 'react-icons/hi2';
import Button from '../../Form/Button';
import ButtonPrimary from '../../Form/ButtonPrimary';
import { useCartHook } from '../../../Hooks/CartHook';
import { useStoreHook } from '../../../Hooks/StoreHook';

interface AddProductFooterButtonsProps {
    product: any;
    currentPrice: number;
    loading: boolean;
    onCancel: CallableFunction;
    onUpdateProduct: CallableFunction;
    onAdd: CallableFunction;
    onRequiredStep: CallableFunction;
    className?: string;
}

const AddProductFooterButtons = ({
    product,
    currentPrice,
    loading,
    onCancel,
    onUpdateProduct,
    onAdd,
    onRequiredStep,
    className=''
}: AddProductFooterButtonsProps) => {

    const { t } = useTranslation();
    const { isStoreCounter } = useOrderTypeContext();
    const { cashier } = useStoreContext();
    const { cartDisplay, disabledButtonAddProduct, cartFinished, requiredStepsValid, allStepsExtrasValid, allStepsIsExtras } = useCartContext();
    const { compositeProductBlocked, loadingCallWaiter } = useAddProductContext();
    const { handleCallWaiter } = useCartHook();
    const { checkCompositeProductBlocked } = useStoreHook();

    const [isBlockedMessage, setIsBlockedMessage] = useState<boolean>(compositeProductBlocked && product.type === PRODUCT_TYPE_COMPOSITE);

    const hasRequiredStep = ():boolean => {
        return (
                (product.type === PRODUCT_TYPE_COMPOSITE && !allStepsIsExtras() && !requiredStepsValid())
                || (product.type === PRODUCT_TYPE_COMPOSITE && allStepsIsExtras() && (!allStepsExtrasValid() || !requiredStepsValid()))
            )
    }

    const handleAdd = () => {
        if (hasRequiredStep()) {
            onRequiredStep();
        } else {
            onAdd();
        }
    }

    useEffect(() => {
        checkCompositeProductBlocked(product).then((res) => {
            setIsBlockedMessage(res);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [product?.id, cartDisplay?.id])

    return (
        <div className={`flex flex-col w-full p-3 md:p-4 z-40 bg-white/75 backdrop-blur-sm sticky bottom-0 ${className}`}>
            <div className={`flex gap-2 w-full justify-end text-sm ${!cashier && 'justify-end'}`}>

                {isStoreCounter && cashier && isBlockedMessage && cartDisplay && !cartFinished && (
                    <ButtonPrimary disabled={loadingCallWaiter} onClick={() => handleCallWaiter({message: `QUERO O ${product?.name}`})} className='w-full md:w-fit'>
                        {t('products.composite_product_blocked_modal_message')}
                    </ButtonPrimary>
                )}

                {(isStoreCounter && cashier) &&
                    <>
                        {!cartDisplay 
                            ?   <ButtonOpenStoreCounter forceLabel proofPayment={true} label={t('proof_payment.button_open_new_comanda_label')} />
                            :
                                !isBlockedMessage && (
                                    <>
                                        <div className='hidden md:block w-2/5' />
                                        {product.type === PRODUCT_TYPE_SIMPLE &&
                                            <ButtonsIncDec disabled={loading} onInc={onUpdateProduct} onDec={onUpdateProduct} className='md:w-auto min-w-28' />
                                        }
                                        <ButtonAddProduct
                                            currentPrice={currentPrice}
                                            disabledButtonAddProduct={disabledButtonAddProduct || isBlockedMessage}
                                            onHandleAdd={handleAdd}
                                            className={`${hasRequiredStep() || isBlockedMessage ? 'opacity-50' : ''}`}
                                        />
                                    </>
                                )
                        }
                    </>
                }

                {!isStoreCounter &&
                    <>
                        {product.type === PRODUCT_TYPE_SIMPLE &&
                            <ButtonsIncDec disabled={loading} onInc={onUpdateProduct} onDec={onUpdateProduct} className='md:w-auto min-w-28' />
                        }
                        <ButtonAddProduct
                            currentPrice={product?.quantity * currentPrice}
                            disabledButtonAddProduct={disabledButtonAddProduct || isBlockedMessage}
                            onHandleAdd={handleAdd}
                            className={`${hasRequiredStep() || isBlockedMessage ? 'opacity-50' : ''}`}
                        />
                    </>
                }
                <Button rounded={((isStoreCounter && cashier) || !isStoreCounter)} className={`${isStoreCounter && !cashier && 'w-full'}`} onClick={onCancel}>
                    {((isStoreCounter && !cashier)) ? t('add_product.close_button_label') : <div><HiXMark size={20} /></div>}
                </Button>
            </div>
        </div>
    );
};

export default AddProductFooterButtons;
